// apps/web/src/shell/menu.ts
//
// Permission-aware navigation tree (#92). Each menu item declares the
// PermissionCode required to see it. The legacy role-array model
// (`roles: [Role.ADMIN, ...]`) is gone — see `permissions.ts` for the catalog.
import type { PermissionCode, User } from '@magimp/shared';
import type { IconName } from '../ui/index.js';
import type { PermissionsBundle } from '../stores/auth.js';
import { hasPermission } from '../stores/auth.js';

const NAV_GROUPS_DATA = [
  {
    id: 'work',
    label: 'nav.groups.work',
    items: [
      {
        id: 'tasks',
        icon: 'check-square',
        label: 'nav.items.tasks',
        route: '/my-day',
        permission: 'tasks.read',
      },
      {
        id: 'kp',
        icon: 'file-text',
        label: 'nav.items.commercialProposals',
        route: '/kp',
        permission: 'commercialProposals.read',
      },
      {
        id: 'queues',
        icon: 'layout-list',
        label: 'nav.items.queues',
        route: '/queues',
        permission: 'stages.read',
      },
      {
        id: 'calls',
        icon: 'phone-call',
        label: 'nav.items.calls',
        route: '/call-center',
        permission: 'calls.read',
      },
      {
        id: 'sla',
        icon: 'gauge',
        label: 'nav.items.sla',
        route: '/dashboard',
        permission: 'audit.read',
      },
    ],
  },
  {
    id: 'crm',
    label: 'nav.groups.crm',
    items: [
      {
        id: 'leads',
        icon: 'user-plus',
        label: 'nav.items.leads',
        route: '/leads',
        permission: 'leads.read',
      },
      {
        id: 'deals',
        icon: 'handshake',
        label: 'nav.items.deals',
        route: '/deals',
        permission: 'deals.read',
      },
      {
        id: 'clients',
        icon: 'users',
        label: 'nav.items.clients',
        route: '/clients',
        permission: 'clients.read',
      },
    ],
  },
  // WHY (#1587): leadership "Dashboards" group. Phase 1 ships a single item
  //   gated on `dashboard.read`. No role holds that code yet (catalog-grow
  //   only), so only admin sees this group via the `ALL` sentinel — the
  //   intended admin-only preview. The `executive` role grant lands in
  //   Phase 2 (#1591).
  {
    id: 'dashboards',
    label: 'nav.groups.dashboards',
    items: [
      {
        id: 'chinaRequestsDashboard',
        icon: 'bar-chart-3',
        label: 'nav.items.chinaRequestsDashboard',
        route: '/dashboards/china-requests',
        permission: 'dashboard.read',
      },
      {
        id: 'agent',
        icon: 'message-square',
        label: 'nav.items.agent',
        route: '/dashboards/agent',
        permission: 'dashboard.read',
      },
      {
        id: 'myDashboards',
        icon: 'kanban',
        label: 'nav.items.myDashboards',
        route: '/dashboards/my',
        permission: 'dashboard.read',
      },
    ],
  },
  // WHY (#698): the new "Logistics" group bundles the four logistics-side
  //   entries together: chinaRates (legacy `/requests` renamed to
  //   `/china-rates`, gated by `requests.read`), russiaRates (new
  //   placeholder gated by `russiaRequests.read`), warehousesChina (new
  //   placeholder gated by `warehousesChina.read`), and cargo (legacy
  //   `/shipments` renamed to `/cargo`, gated by `cargo.read`). Order
  //   matches the issue contract verbatim.
  {
    id: 'logistics',
    label: 'nav.groups.logistics',
    items: [
      {
        id: 'chinaRates',
        icon: 'package',
        label: 'nav.items.chinaRates',
        route: '/china-rates',
        permission: 'requests.read',
      },
      {
        id: 'russiaRates',
        icon: 'package',
        label: 'nav.items.russiaRates',
        route: '/russia-rates',
        permission: 'russiaRequests.read',
      },
      {
        id: 'russiaCarriers',
        icon: 'ship',
        label: 'nav.items.russiaCarriers',
        route: '/russia-carriers',
        permission: 'russiaCarriers.read',
      },
      {
        id: 'warehousesChina',
        icon: 'warehouse',
        label: 'nav.items.warehousesChina',
        route: '/warehouses-china',
        permission: 'warehousesChina.read',
      },
      {
        id: 'cargo',
        icon: 'truck',
        label: 'nav.items.cargo',
        route: '/cargo',
        permission: 'cargo.read',
      },
      {
        id: 'cargoProblems',
        icon: 'alert-triangle',
        label: 'nav.items.cargoProblems',
        route: '/cargo/problems',
        permission: 'cargoProblems.read',
      },
      {
        id: 'logisticsCalculations',
        icon: 'file-text',
        label: 'nav.items.logisticsCalculations',
        route: '/ttn',
        permission: 'logisticsCalculations.read',
      },
    ],
  },
  {
    id: 'finance',
    label: 'nav.groups.finance',
    items: [
      {
        id: 'revenue',
        icon: 'bar-chart-3',
        label: 'nav.items.revenue',
        route: '/finance/revenue',
        requireAll: true,
      },
      {
        id: 'payments',
        icon: 'banknote',
        label: 'nav.items.payments',
        route: '/finance/payment-claims',
        permission: 'paymentClaims.read',
      },
    ],
  },
  {
    id: 'refs',
    label: 'nav.groups.refs',
    items: [
      {
        id: 'carriers',
        icon: 'ship',
        label: 'nav.items.carriers',
        route: '/carriers',
        permission: 'carriers.read',
      },
      {
        id: 'warehouses',
        icon: 'warehouse',
        label: 'nav.items.warehouses',
        route: '/warehouses',
        permission: 'warehouses.read',
      },
      {
        id: 'prices',
        icon: 'tag',
        label: 'nav.items.prices',
        route: '/price-lists',
        permission: 'priceLists.read',
      },
      {
        id: 'transportTypes',
        icon: 'truck',
        label: 'nav.items.transportTypes',
        route: '/transport-types',
        permission: 'transportTypes.read',
      },
      {
        id: 'arrivalCities',
        icon: 'map-pin',
        label: 'nav.items.arrivalCities',
        route: '/arrival-cities',
        permission: 'arrivalCities.read',
      },
      {
        id: 'roadOptions',
        icon: 'globe',
        label: 'nav.items.roadOptions',
        route: '/road-options',
        permission: 'roads.manage',
      },
      {
        id: 'packagingOptions',
        icon: 'package',
        label: 'nav.items.packagingOptions',
        route: '/packaging-options',
        permission: 'packaging.manage',
      },
      {
        id: 'sources',
        icon: 'radio',
        label: 'nav.items.sources',
        route: '/sources',
        permission: 'sources.read',
      },
      {
        id: 'stages',
        icon: 'git-branch',
        label: 'nav.items.stages',
        route: '/stages',
        permission: 'stages.read',
      },
    ],
  },
  {
    id: 'admin',
    label: 'nav.groups.admin',
    items: [
      {
        id: 'users',
        icon: 'user',
        label: 'nav.items.users',
        route: '/users',
        permission: 'users.manage',
      },
      {
        id: 'depts',
        icon: 'users-round',
        label: 'nav.items.depts',
        route: '/departments',
        permission: 'departments.manage',
      },
      {
        id: 'roles',
        icon: 'shield',
        label: 'nav.items.roles',
        route: '/roles',
        permission: 'roles.read',
      },
      {
        id: 'audit',
        icon: 'scroll-text',
        label: 'nav.items.audit',
        route: '/audit',
        permission: 'audit.read',
      },
      {
        id: 'crmIntakeAdmin',
        icon: 'phone-call',
        label: 'nav.items.crmIntakeAdmin',
        route: '/crm-intake-admin',
        requireAll: true,
      },
    ],
  },
] as const satisfies readonly {
  id: string;
  label: string;
  items: readonly (
    | {
        id: string;
        icon: IconName;
        label: string;
        route: string;
        permission: PermissionCode;
      }
    | {
        id: string;
        icon: IconName;
        label: string;
        route: string;
        requireAll: true;
      }
  )[];
}[];

// WHY: derive label literal unions from the const data so callers can pass
// them to typed `t()` directly. Adding a new entry to NAV_GROUPS_DATA
// extends these unions automatically.
export type NavGroupLabel = (typeof NAV_GROUPS_DATA)[number]['label'];
export type NavItemLabel = (typeof NAV_GROUPS_DATA)[number]['items'][number]['label'];

type MenuItemBase = {
  id: string;
  icon: IconName;
  label: NavItemLabel;
  route: string;
};
// INVARIANT (#602): a menu item is gated by EITHER a single
// `PermissionCode` OR the strict admin sentinel — never both, never
// neither. The discriminated union forces every new entry to declare
// one access mode explicitly; the runtime filter relies on this.
export type MenuItem =
  | (MenuItemBase & { permission: PermissionCode; requireAll?: undefined })
  | (MenuItemBase & { permission?: undefined; requireAll: true });
export type MenuGroup = {
  id: string;
  label: NavGroupLabel;
  items: MenuItem[];
};

export const NAV_GROUPS: readonly MenuGroup[] = NAV_GROUPS_DATA.map((g) => ({
  id: g.id,
  label: g.label,
  items: g.items.map(
    (it): MenuItem =>
      'permission' in it
        ? {
            id: it.id,
            icon: it.icon,
            label: it.label,
            route: it.route,
            permission: it.permission,
          }
        : {
            id: it.id,
            icon: it.icon,
            label: it.label,
            route: it.route,
            requireAll: true,
          },
  ),
}));

export function menuForPermissions(perms: PermissionsBundle): MenuGroup[] {
  return NAV_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((it) =>
      it.requireAll === true ? perms === 'ALL' : hasPermission(perms, it.permission),
    ),
  })).filter((g) => g.items.length > 0);
}

// WHY (#623, #698): admin → /my-day, anyone with requests.read → /china-rates
// (the route was renamed from /requests to /china-rates in #698 but the
// gating permission code stayed the same). Bundles without requests.read
// (notably the `logist_ru` role: transportTypes + arrivalCities +
// russiaRequests + cargo + warehousesChina, but no `requests.read`) fall
// back to the first menu-visible item, since the legacy /requests redirect
// would have sent them to a forbidden route. Last fallback is /profile for
// the pathological case of zero menu-visible permissions.
//
// WHY (#1183): permission-only helper, kept for callers that do not yet
// have a User in hand. Manager-system landing carve-out lives in
// `landingForUser` — the system `manager` role has `requests.read` but
// must land on /my-day instead of /china-rates.
export function landingForPermissions(perms: PermissionsBundle): string {
  if (perms === 'ALL') return '/my-day';
  if (hasPermission(perms, 'requests.read')) return '/china-rates';
  const visible = menuForPermissions(perms);
  const firstGroup = visible[0];
  const firstItem = firstGroup?.items[0];
  return firstItem?.route ?? '/profile';
}

// WHY (#1183): identifies the SEEDED system `manager` role. The Manager-
//   only menu/landing/route policy below applies ONLY when both
//   `user.role.name === 'manager'` AND `user.role.isSystem === true`.
//   A custom non-system role named 'manager', or any role with a
//   manager-like permission bundle, must NOT receive these overrides —
//   backend RBAC stays authoritative for what a token can do; this
//   predicate gates only frontend UX shape.
export function isManagerSystemRole(user: User | null | undefined): boolean {
  return user?.role?.name === 'manager' && user?.role?.isSystem === true;
}

// WHY (#1183): Manager-system frontend menu policy. Locks both group order
//   and item include-set per the issue contract: work→tasks; crm→leads,
//   deals, clients; logistics→cargo, chinaRates, russiaRates,
//   warehousesChina; refs→warehouses, prices. The include-set is the
//   CEILING; per-item `permission` from NAV_GROUPS stays the FLOOR —
//   `menuForManager` retains an item only if Manager actually holds the
//   underlying permission. INVARIANT: adding a new grant to
//   `MANAGER_PERMISSIONS` in seed does NOT surface a new menu item
//   automatically — the include-set is the single source of truth for
//   what Manager sees. Update this constant when the Manager menu
//   contract changes.
const MANAGER_MENU_POLICY: ReadonlyArray<{
  groupId: string;
  itemIds: ReadonlyArray<string>;
}> = [
  { groupId: 'work', itemIds: ['tasks', 'kp'] },
  { groupId: 'crm', itemIds: ['leads', 'deals', 'clients'] },
  {
    groupId: 'logistics',
    itemIds: [
      'cargo',
      'cargoProblems',
      'logisticsCalculations',
      'chinaRates',
      'russiaRates',
      'warehousesChina',
    ],
  },
  { groupId: 'refs', itemIds: ['warehouses', 'prices'] },
];

// WHY (#1183, #1549): shared policy applicator for the system-role menus.
//   Walks the policy in order, resolving each `{groupId, itemIds}` against
//   NAV_GROUPS. The include-set is the CEILING (only listed items can
//   appear, in the listed order); each item's `permission`/`requireAll`
//   from NAV_GROUPS stays the FLOOR — an item is kept only if the bundle
//   actually grants it. Empty groups are dropped.
function applyMenuPolicy(
  perms: PermissionsBundle,
  policy: ReadonlyArray<{ groupId: string; itemIds: ReadonlyArray<string> }>,
): MenuGroup[] {
  const groupById = new Map(NAV_GROUPS.map((g) => [g.id, g] as const));
  const groups: MenuGroup[] = [];
  for (const entry of policy) {
    const source = groupById.get(entry.groupId);
    if (!source) continue;
    const itemById = new Map(source.items.map((it) => [it.id, it] as const));
    const items: MenuItem[] = [];
    for (const itemId of entry.itemIds) {
      const item = itemById.get(itemId);
      if (!item) continue;
      const granted =
        item.requireAll === true ? perms === 'ALL' : hasPermission(perms, item.permission);
      if (granted) items.push(item);
    }
    if (items.length > 0) {
      groups.push({ id: source.id, label: source.label, items });
    }
  }
  return groups;
}

function menuForManager(perms: PermissionsBundle): MenuGroup[] {
  return applyMenuPolicy(perms, MANAGER_MENU_POLICY);
}

// WHY (#1549, mirrors the #1183 manager-system pattern): identifies the SEEDED system `logist_ru` role. The
//   logist_ru-only menu policy below applies ONLY when both
//   `user.role.name === 'logist_ru'` AND `user.role.isSystem === true`.
//   A custom non-system role named 'logist_ru' must NOT receive the
//   override — backend RBAC stays authoritative; this predicate gates
//   only frontend sidebar shape.
/**
 * @aiSecurity backend RBAC is authoritative. This predicate only gates the
 *   logist_ru sidebar shaping in `menuForLogistRu`; direct route access is
 *   still enforced by `PermissionGate`.
 */
export function isLogistRuSystemRole(user: User | null | undefined): boolean {
  return user?.role?.name === 'logist_ru' && user?.role?.isSystem === true;
}

// WHY (#1549): logist_ru-system frontend menu policy. Locks the sidebar to
//   EXACTLY {work→tasks; logistics→russiaRates, russiaCarriers, cargo} in
//   that visual order. The include-set is the CEILING; per-item
//   `permission` from NAV_GROUPS stays the FLOOR. `warehousesChina` is
//   DELIBERATELY excluded even though `logist_ru` retains
//   `warehousesChina.read` — the item is hidden via policy, not by
//   revoking the permission. INVARIANT: adding a new grant to
//   `LOGIST_RU_PERMISSIONS` in seed does NOT auto-surface a menu item;
//   this constant is the single source of truth for what logist_ru sees.
const LOGIST_RU_MENU_POLICY: ReadonlyArray<{
  groupId: string;
  itemIds: ReadonlyArray<string>;
}> = [
  { groupId: 'work', itemIds: ['tasks'] },
  { groupId: 'logistics', itemIds: ['russiaRates', 'russiaCarriers', 'cargo', 'cargoProblems'] },
];

function menuForLogistRu(perms: PermissionsBundle): MenuGroup[] {
  return applyMenuPolicy(perms, LOGIST_RU_MENU_POLICY);
}

/**
 * @aiPurpose user-aware menu helper. For the system `manager` role, returns
 *   the locked Manager menu (see `MANAGER_MENU_POLICY`); for the system
 *   `logist_ru` role, the locked logist_ru menu (see
 *   `LOGIST_RU_MENU_POLICY`); for everyone else, delegates to
 *   `menuForPermissions`.
 * @aiSecurity backend RBAC is authoritative. This helper only shapes
 *   sidebar visibility — direct route access is still gated by
 *   `PermissionGate` (+ optional `denySystemRoles`).
 */
export function menuForUser(user: User | null | undefined, perms: PermissionsBundle): MenuGroup[] {
  if (isManagerSystemRole(user)) return menuForManager(perms);
  if (isLogistRuSystemRole(user)) return menuForLogistRu(perms);
  return menuForPermissions(perms);
}

/**
 * @aiPurpose user-aware landing helper. The external `call_center_agent`
 *   role always lands on `/call-center/intake` (#1259 — confined external
 *   surface, never an internal route). System `manager` always lands on
 *   `/my-day`; everyone else falls back to `landingForPermissions`.
 *   Non-Manager users with `requests.read` keep landing on `/china-rates`.
 */
export function landingForUser(user: User | null | undefined, perms: PermissionsBundle): string {
  if (user?.role?.name === 'call_center_agent') return '/call-center/intake';
  if (isManagerSystemRole(user)) return '/my-day';
  return landingForPermissions(perms);
}

/**
 * @aiPurpose single longest-prefix-match lookup against a menu tree, returning
 *   the matched `{ group, item }` pair. Replaces three nearly-identical
 *   per-component implementations in `Sidebar.tsx` (returned item id),
 *   `Topbar.tsx` (returned parent group label), and `Placeholder.tsx`
 *   (returned the item itself). Adding a new menu entry now updates all
 *   three call sites in lockstep.
 * @aiInvariant matching uses `pathname === item.route ||
 *   pathname.startsWith(item.route + '/')` — the trailing `/` guard prevents
 *   `/foo` from matching `/foobar`. When multiple items match (a parent route
 *   and a nested child route), the longest `item.route` wins. Returns `null`
 *   for paths outside the tree.
 */
export function findActiveMenuMatch(
  pathname: string,
  groups: readonly MenuGroup[],
): { group: MenuGroup; item: MenuItem } | null {
  let best: { group: MenuGroup; item: MenuItem } | null = null;
  for (const group of groups) {
    for (const item of group.items) {
      if (pathname === item.route || pathname.startsWith(item.route + '/')) {
        if (!best || item.route.length > best.item.route.length) {
          best = { group, item };
        }
      }
    }
  }
  return best;
}
